/**
 * Shared cart rendering — item rows, qty/remove controls, and the click
 * dispatcher powering them. Used by <khaime-cart-page> today; the drawer
 * still carries its own copy (kept intentionally to avoid a CSS rename on
 * a stable component — revisit later).
 *
 * Class prefix: `ks-cart-*` (drawer uses `ks-drawer-*`).
 */

import type { LocalCartItem } from '../../types';
import { formatPrice } from '../../types';
import { escapeHtml } from '../../utils';
import type { CartManager } from '../../utils/cart-manager';

export type CartItemVariant = 'full' | 'compact';

export interface RenderCartItemOptions {
  variant?: CartItemVariant;
  currency: string;
}

export function renderCartItemRow(item: LocalCartItem, opts: RenderCartItemOptions): string {
  const variant = opts.variant ?? 'full';
  const unitPrice = item.price || 0;
  const lineTotal = unitPrice * item.quantity;
  const displayPrice = item.formatted_price
    ? item.quantity === 1
      ? item.formatted_price
      : formatPrice(lineTotal / 100, opts.currency)
    : formatPrice(lineTotal / 100, opts.currency);

  const pid = item.product_id;
  const vid = item.variant_id || '';

  const thumb = item.thumbnail
    ? `<img class="ks-cart-item-img" src="${escapeHtml(item.thumbnail)}" alt="${escapeHtml(item.title)}" />`
    : `<div class="ks-cart-item-placeholder" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>`;

  const qtyControls = `
    <div class="ks-cart-item-qty">
      <button class="ks-cart-qty-btn" data-action="qty-dec" data-product-id="${pid}" data-variant-id="${vid}" aria-label="Decrease quantity">-</button>
      <span class="ks-cart-qty-value">${item.quantity}</span>
      <button class="ks-cart-qty-btn" data-action="qty-inc" data-product-id="${pid}" data-variant-id="${vid}" aria-label="Increase quantity">+</button>
    </div>
  `;

  const removeBtn = `
    <button class="ks-cart-item-remove" data-action="remove" data-product-id="${pid}" data-variant-id="${vid}" aria-label="Remove from cart">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  `;

  const variantLabel = item.variant_name
    ? `<div class="ks-cart-item-variant">${escapeHtml(item.variant_name)}</div>`
    : '';

  if (variant === 'compact') {
    return `
      <div class="ks-cart-item ks-cart-item--compact" data-product-id="${pid}" data-variant-id="${vid}">
        ${thumb}
        <div class="ks-cart-item-info">
          <div class="ks-cart-item-title">${escapeHtml(item.title)}</div>
          ${variantLabel}
          <div class="ks-cart-item-meta">
            <span class="ks-cart-item-qty-compact">×${item.quantity}</span>
            <span class="ks-cart-item-price">${displayPrice}</span>
          </div>
        </div>
        ${removeBtn}
      </div>
    `;
  }

  return `
    <div class="ks-cart-item" data-product-id="${pid}" data-variant-id="${vid}">
      ${thumb}
      <div class="ks-cart-item-info">
        <div class="ks-cart-item-title">${escapeHtml(item.title)}</div>
        ${variantLabel}
        <div class="ks-cart-item-controls">
          ${qtyControls}
          <span class="ks-cart-item-price">${displayPrice}</span>
        </div>
      </div>
      ${removeBtn}
    </div>
  `;
}

export interface RenderEmptyOptions {
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaAction?: string;
  compact?: boolean;
}

export function renderCartEmptyState(opts: RenderEmptyOptions = {}): string {
  const title = opts.title ?? 'Your cart is empty';
  const subtitle = opts.subtitle ?? 'Browse products to add items';
  const cta = opts.ctaLabel
    ? `<button class="ks-cart-empty-cta" data-action="${opts.ctaAction ?? 'continue'}">${escapeHtml(opts.ctaLabel)}</button>`
    : '';

  if (opts.compact) {
    return `<div class="ks-cart-empty ks-cart-empty--compact">${escapeHtml(title)}</div>`;
  }

  return `
    <div class="ks-cart-empty">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
      <div class="ks-cart-empty-text">${escapeHtml(title)}</div>
      <div class="ks-cart-empty-sub">${escapeHtml(subtitle)}</div>
      ${cta}
    </div>
  `;
}

/**
 * Handle a click on an element decorated with `data-action`. Returns the
 * action name so the caller can react to `checkout` / `close` / `continue`
 * while qty/remove mutations are applied here.
 */
export function handleCartAction(
  event: Event,
  cartManager: CartManager,
): string | null {
  const target = (event.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
  if (!target) return null;

  const action = target.dataset.action;
  if (!action) return null;

  const productId = target.dataset.productId ? Number(target.dataset.productId) : undefined;
  const rawVariantId = target.dataset.variantId;
  const variantId: number | string | undefined = rawVariantId
    ? isNaN(Number(rawVariantId))
      ? rawVariantId
      : Number(rawVariantId)
    : undefined;

  switch (action) {
    case 'qty-dec':
      if (productId != null) {
        const item = cartManager.getItems().find(
          (i) => i.product_id === productId && (variantId == null || i.variant_id === variantId),
        );
        if (item && item.quantity > 1) {
          cartManager.updateQuantity(productId, item.quantity - 1, variantId);
        } else if (item) {
          cartManager.removeItem(productId, variantId);
        }
      }
      break;
    case 'qty-inc':
      if (productId != null) {
        const item = cartManager.getItems().find(
          (i) => i.product_id === productId && (variantId == null || i.variant_id === variantId),
        );
        if (item && item.quantity < 99) {
          cartManager.updateQuantity(productId, item.quantity + 1, variantId);
        }
      }
      break;
    case 'remove':
      if (productId != null) {
        cartManager.removeItem(productId, variantId);
      }
      break;
  }

  return action;
}

export const CART_SHARED_STYLES = `
  /* ——— Item row (full) ——— */
  .ks-cart-item {
    display: grid;
    grid-template-columns: 112px 1fr auto;
    gap: 24px;
    padding: 28px 0;
    /* Adaptive hairline: uses inherited text color so it contrasts
       correctly on both light and dark WP page backgrounds. */
    border-bottom: 1px solid color-mix(in srgb, currentColor 14%, transparent);
    align-items: start;
    position: relative;
    transition: background 240ms ease;
  }

  .ks-cart-item:last-child { border-bottom: none; }

  .ks-cart-item-img,
  .ks-cart-item-placeholder {
    width: 112px;
    height: 112px;
    border-radius: 3px;
    object-fit: cover;
    background: var(--ksp-card-bg);
    flex-shrink: 0;
    border: 1px solid var(--ksp-hairline);
    transition: transform 420ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .ks-cart-item:hover .ks-cart-item-img { transform: scale(1.015); }

  .ks-cart-item--compact .ks-cart-item-img,
  .ks-cart-item--compact .ks-cart-item-placeholder {
    width: 64px;
    height: 64px;
    border-radius: 2px;
  }

  .ks-cart-item-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--ksp-muted-2);
  }

  .ks-cart-item-placeholder svg {
    width: 28px;
    height: 28px;
    opacity: 0.5;
  }

  .ks-cart-item-info {
    min-width: 0;
    padding-top: 4px;
  }

  .ks-cart-item-title {
    font-family: var(--ksp-font-display);
    font-size: 1.125rem;
    font-weight: 500;
    font-variation-settings: 'opsz' 24;
    letter-spacing: -0.012em;
    /* Page-level item — inherit host text color */
    color: inherit;
    margin-bottom: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.25;
  }

  .ks-cart-item--compact .ks-cart-item-title {
    font-size: 0.9375rem;
    -webkit-line-clamp: 1;
    margin-bottom: 2px;
  }

  .ks-cart-item-variant {
    font-size: 0.8125rem;
    color: inherit;
    opacity: 0.65;
    margin-bottom: 18px;
    letter-spacing: 0.01em;
  }

  .ks-cart-item--compact .ks-cart-item-variant {
    font-size: 0.75rem;
    margin-bottom: 4px;
  }

  .ks-cart-item-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    margin-top: 4px;
  }

  .ks-cart-item-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
  }

  .ks-cart-item-qty-compact {
    font-size: 0.75rem;
    color: var(--ksp-muted);
    letter-spacing: 0.04em;
  }

  /* Quantity stepper — hairline pill */
  .ks-cart-item-qty {
    display: inline-flex;
    align-items: center;
    border: 1px solid color-mix(in srgb, currentColor 22%, transparent);
    border-radius: 999px;
    overflow: hidden;
    background: transparent;
    height: 36px;
    transition: border-color 220ms ease;
  }

  .ks-cart-item-qty:hover {
    border-color: color-mix(in srgb, currentColor 55%, transparent);
  }

  .ks-cart-qty-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: inherit;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 400;
    font-family: inherit;
    opacity: 0.75;
    transition: opacity 180ms ease, background 180ms ease;
  }

  .ks-cart-qty-btn:hover {
    background: color-mix(in srgb, currentColor 8%, transparent);
    opacity: 1;
  }

  .ks-cart-qty-value {
    min-width: 28px;
    text-align: center;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 0 4px;
    color: inherit;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.01em;
  }

  /* Price — right aligned in grid col 3 */
  .ks-cart-item-price {
    font-family: var(--ksp-font-display);
    font-size: 1.0625rem;
    font-weight: 500;
    font-variation-settings: 'opsz' 24;
    color: inherit;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.005em;
    text-align: right;
    white-space: nowrap;
  }

  .ks-cart-item--compact {
    grid-template-columns: 64px 1fr auto;
    gap: 14px;
    padding: 16px 0;
    align-items: center;
  }

  /* Narrow container: shrink thumbnail + stack title/price for legibility */
  @container kc-page (max-width: 480px) {
    .ks-cart-item {
      grid-template-columns: 84px 1fr auto;
      gap: 16px;
      padding: 22px 0;
    }
    .ks-cart-item-img,
    .ks-cart-item-placeholder {
      width: 84px;
      height: 84px;
    }
    .ks-cart-item-title { font-size: 1rem; }
    .ks-cart-item-controls {
      flex-wrap: wrap;
      gap: 12px;
    }
  }

  .ks-cart-item-remove {
    background: none;
    border: none;
    color: var(--ksp-muted-2);
    cursor: pointer;
    padding: 6px;
    border-radius: 999px;
    align-self: start;
    flex-shrink: 0;
    transition: color 200ms ease, background 200ms ease, transform 200ms ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
  }

  .ks-cart-item-remove:hover {
    color: var(--ksp-danger, #a24b3a);
    background: rgba(162, 75, 58, 0.06);
    transform: rotate(90deg);
  }

  .ks-cart-item-remove svg {
    width: 14px;
    height: 14px;
    stroke-width: 1.75;
  }

  /* ——— Empty state ——— */
  .ks-cart-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 96px 32px;
    text-align: center;
    color: inherit;
    border: 1px solid var(--ksp-hairline);
    border-radius: var(--ksp-radius-md);
    background:
      radial-gradient(ellipse at top, color-mix(in srgb, var(--ksp-accent) 6%, transparent), transparent 65%),
      var(--ksp-card-bg);
    position: relative;
    overflow: hidden;
    animation: kc-fade-up 620ms cubic-bezier(0.22, 0.61, 0.36, 1) 120ms both;
  }

  .ks-cart-empty::before,
  .ks-cart-empty::after {
    content: '';
    position: absolute;
    width: 1px;
    background: var(--ksp-hairline);
    opacity: 0.7;
  }

  .ks-cart-empty::before { top: 24px; bottom: 24px; left: 24px; }
  .ks-cart-empty::after { top: 24px; bottom: 24px; right: 24px; }

  .ks-cart-empty svg {
    width: 44px;
    height: 44px;
    margin-bottom: 28px;
    color: var(--ksp-accent);
    opacity: 0.85;
    stroke-width: 1.25;
  }

  .ks-cart-empty-text {
    font-family: var(--ksp-font-display);
    font-size: clamp(1.75rem, 4vw, 2.25rem);
    font-weight: 400;
    font-variation-settings: 'opsz' 144;
    line-height: 1.05;
    letter-spacing: -0.018em;
    margin-bottom: 10px;
    color: inherit;
  }

  .ks-cart-empty-sub {
    font-size: 0.9375rem;
    color: var(--ksp-muted);
    max-width: 360px;
    line-height: 1.55;
  }

  .ks-cart-empty-cta {
    margin-top: 32px;
    padding: 14px 32px;
    background: var(--ksp-cta-bg);
    color: var(--ksp-cta-ink);
    border: none;
    border-radius: var(--ksp-btn-radius);
    font-family: var(--ksp-font-body);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 260ms ease, letter-spacing 260ms ease;
  }

  .ks-cart-empty-cta:hover {
    background: var(--ksp-accent);
    letter-spacing: 0.2em;
  }

  .ks-cart-empty--compact {
    padding: 40px 20px;
    text-align: center;
    font-family: var(--ksp-font-display);
    font-size: 0.9375rem;
    color: var(--ksp-muted);
    font-style: italic;
  }
`;
